import type { StateCreator } from 'zustand';
import type { Perspective, ThreadSpace } from '../../types';
import type { VoyagerState } from '../useVoyagerStore';
import { getAllPerspectives } from '../../apis';
import { generateId } from '../../utils';
import {
  loadSpaces,
  persistSpaces,
  loadPinnedPerspectives,
  PINNED_STORAGE_KEY,
} from '../helpers/persistenceHelpers';

// Threads moved to the server in #181 (`threadsSlice`). Spaces — a person's
// own grouping of History — are still a localStorage convenience.
export interface LandingSlice {
  allPerspectives: Perspective[];
  pinnedPerspectives: string[];

  spaces: ThreadSpace[];

  fetchAllPerspectives: () => Promise<void>;
  togglePinPerspective: (id: string) => void;

  createSpace: (name: string) => ThreadSpace;
  renameSpace: (spaceId: string, name: string) => void;
  deleteSpace: (spaceId: string) => void;
}

export const createLandingSlice: StateCreator<
  VoyagerState, [], [], LandingSlice
> = (set, get) => ({
  allPerspectives: [],
  pinnedPerspectives: loadPinnedPerspectives(),

  spaces: loadSpaces(),

  fetchAllPerspectives: async () => {
    try {
      const allPerspectives = await getAllPerspectives();
      set({ allPerspectives });
    } catch (err: any) {
      console.error('Failed to fetch all perspectives:', err?.message);
    }
  },

  togglePinPerspective: (id: string) => {
    const { pinnedPerspectives } = get();
    const next = pinnedPerspectives.includes(id)
      ? pinnedPerspectives.filter((p) => p !== id)
      : [...pinnedPerspectives, id];
    localStorage.setItem(PINNED_STORAGE_KEY, JSON.stringify(next));
    set({ pinnedPerspectives: next });
  },

  createSpace: (name: string): ThreadSpace => {
    const { spaces } = get();
    const newSpace: ThreadSpace = { id: generateId(), name, createdAt: new Date().toISOString() };
    const newSpaces = [...spaces, newSpace];
    persistSpaces(newSpaces);
    set({ spaces: newSpaces });
    return newSpace;
  },

  renameSpace: (spaceId: string, name: string) => {
    const { spaces } = get();
    const newSpaces = spaces.map((s) => (s.id === spaceId ? { ...s, name } : s));
    persistSpaces(newSpaces);
    set({ spaces: newSpaces });
  },

  deleteSpace: (spaceId: string) => {
    const { spaces, savedThreads } = get();
    const newSpaces = spaces.filter((s) => s.id !== spaceId);
    persistSpaces(newSpaces);
    set({ spaces: newSpaces });
    // Threads in that space drop back to unorganized, on the server too.
    savedThreads
      .filter((t) => t.spaceId === spaceId)
      .forEach((t) => get().addThreadToSpace(t.id, null));
  },
});
